import React from 'react';
import { X } from 'lucide-react';

const PrivacyPolicy = ({ privacyPolicyModalOpen, setPrivacyPolicyModalOpen }) => {
  
  return (
    <>
      {privacyPolicyModalOpen && (
        <div 
            className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setPrivacyPolicyModalOpen(false)}
        >
            <div 
            className="bg-white dark:bg-gray-900 rounded-xl max-w-full w-full sm:max-w-3xl lg:max-w-4xl p-6 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
            onClick={e => e.stopPropagation()}
            >
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                <h3 className="text-2xl font-bold dark:text-white flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Privacy Policy
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Effective Date: {new Date().toLocaleDateString()} | Version 3.2
                </p>
                </div>
                <button 
                onClick={() => setPrivacyPolicyModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close privacy policy"
                >
                <X size={24} />
                </button>
            </div>
            
            <div className="prose dark:prose-invert max-w-none">
                <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1. Scope and Consent</h4>
                <p className="text-gray-700 dark:text-gray-300">
                    This Privacy Policy governs the processing of personal data collected through 0x10LayerFF's digital platforms and services ("Services"). 
                    By using our Services, you consent to the data practices described herein. This policy complies with the EU General Data Protection Regulation (GDPR), 
                    California Consumer Privacy Act (CCPA), and other applicable privacy laws.
                </p>
                </div>

                <div className="grid gap-8">
                <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">2. Data Collection</h4>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300">
                    <div>
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1">Personal Data</h5>
                        <p className="text-sm">
                        We collect identifiers (name, email, phone), professional information (job title, company), and authentication data (credentials, security questions).
                        </p>
                    </div>
                    <div>
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1">Technical Data</h5>
                        <p className="text-sm">
                        Includes IP addresses, device characteristics, browser type, operating system, and usage data collected via cookies and similar technologies.
                        </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Sensitive Data
                        </h5>
                        <p className="text-xs">
                        We do not intentionally collect sensitive personal data (racial/ethnic origin, political opinions, religious beliefs, etc.) unless required by law.
                        </p>
                    </div>
                    </div>
                </div>

                <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">3. Data Processing</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-2">Legal Bases</h5>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li>Contractual necessity (service delivery)</li>
                        <li>Legitimate business interests</li>
                        <li>Legal compliance</li>
                        <li>Consent (for marketing)</li>
                        </ul>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-2">Purposes</h5>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li>Service provisioning and improvement</li>
                        <li>Security incident detection</li>
                        <li>Regulatory compliance</li>
                        <li>Personalized user experience</li>
                        </ul>
                    </div>
                    </div>
                </div>

                <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">4. Data Sharing</h4>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300">
                    <div>
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1">Service Providers</h5>
                        <p className="text-sm">
                        We engage carefully vetted subprocessors for cloud hosting, payment processing, and customer support. All subprocessors undergo security assessments and sign DPAs.
                        </p>
                    </div>
                    <div>
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1">Legal Disclosures</h5>
                        <p className="text-sm">
                        We may disclose information when required by law, such as in response to valid court orders or regulatory requests. We notify users of such requests when permitted.
                        </p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        International Transfers
                        </h5>
                        <p className="text-xs">
                        Data may be transferred outside your jurisdiction under EU Standard Contractual Clauses or other approved mechanisms. Our subprocessor locations are documented in our DPA.
                        </p>
                    </div>
                    </div>
                </div>

                <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">5. Your Rights</h4>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                        { right: "Access", desc: "Request copies of your data" },
                        { right: "Rectification", desc: "Correct inaccurate information" },
                        { right: "Erasure", desc: "Delete your data under certain conditions" },
                        { right: "Restriction", desc: "Limit processing of your data" },
                        { right: "Portability", desc: "Receive your data in a structured format" },
                        { right: "Objection", desc: "Opt-out of certain processing" }
                    ].map((item, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="font-medium text-gray-900 dark:text-white">{item.right}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{item.desc}</div>
                        </div>
                    ))}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-3">
                    To exercise these rights, contact our Data Protection Officer at <span className="text-blue-600 dark:text-blue-400">dpo@0x10layerff.com</span>. We respond within 30 days and may request verification.
                    </p>
                </div>

                <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">6. Security Measures</h4>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300">
                    <p>
                        We implement technical and organizational measures including:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>Encryption of data in transit (TLS 1.3) and at rest (AES-256)</li>
                        <li>Regular penetration testing and vulnerability scanning</li>
                        <li>Role-based access controls with multi-factor authentication</li>
                        <li>SOC 2 Type II certified infrastructure</li>
                    </ul>
                    <div className="bg-yellow-50 dark:bg-yellow-900/10 p-3 rounded-lg border border-yellow-100 dark:border-yellow-900/20">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Breach Notification
                        </h5>
                        <p className="text-xs">
                        In the event of a data breach affecting your information, we will notify you and relevant authorities within 72 hours of discovery as required by law.
                        </p>
                    </div>
                    </div>
                </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Policy Updates</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                    We may update this policy to reflect legal or operational changes. Material changes will be notified via email 30 days in advance. The version history is available upon request.
                </p>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    For questions or to exercise your rights:
                </div>
                <div className="flex gap-3">
                    <button 
                    onClick={() => window.print()}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium"
                    >
                    Print Policy
                    </button>
                    <a
                    href="mailto:privacy@0x10layerff.com"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                    Contact Privacy Team
                    </a>
                </div>
                </div>
            </div>
            </div>
        </div>
        )}
    </>
  );
};

export default PrivacyPolicy;